import { useMemo, useState, type ComponentType } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ClipboardCheck, FilePlus2, ReceiptText } from 'lucide-react';
import { cn } from '@my/ui';
import { useInvoiceViewStore, type InvoiceTab } from '@/stores/invoiceView';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { TmsHistoryButton } from '@/components/HistoryButtons';
import { ForApprovalTab } from './components/ForApprovalTab';
import { CreateInvoicesTab } from './components/CreateInvoicesTab';
import { InvoiceSummaryTab } from './components/InvoiceSummaryTab';
import { FinanceAssistantPanel } from './components/FinanceAssistantPanel';

const TABS: { id: InvoiceTab; label: string; Icon: ComponentType<{ className?: string }> }[] = [
  { id: 'approval', label: 'For approval', Icon: ClipboardCheck },
  { id: 'create', label: 'Create invoices', Icon: FilePlus2 },
  { id: 'summary', label: 'Invoice summary', Icon: ReceiptText },
];

/**
 * Finance → Invoices. Top-centre icon tabs (mirrors the Work View) switch
 * between For approval / Create invoices / Invoice summary; the active tab is
 * persisted. Each tab owns its own actions (Export / MyI) — the assistant
 * panel itself lives here so it can slide over the whole screen.
 */
export function InvoicingPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const currentTab = useInvoiceViewStore((s) => s.currentTab);
  const setCurrentTab = useInvoiceViewStore((s) => s.setCurrentTab);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const openAssistant = () => setAssistantOpen(true);

  // The assistant belongs to the active tab — switching tabs closes it so it
  // never shows another tab's data.
  const goToTab = (tab: InvoiceTab) => {
    setCurrentTab(tab);
    setAssistantOpen(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col p-0">
      {/* Top-centre icon tabs — same pattern as the Work View — with a full-width
          separation line beneath, mirroring the Manifesting screen. */}
      {/* `relative` so the history control can sit at the right edge without
          pulling the tabs off centre — they are the page's anchor. */}
      <div className="relative flex w-full justify-center border-b border-slate-200 pb-2 pt-2">
        <div className="flex rounded-md bg-slate-100 p-1 shadow-sm">
          {TABS.map(({ id, label, Icon }) => {
            const active = currentTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => goToTab(id)}
                aria-label={label}
                title={label}
                className={cn(
                  'inline-flex h-10 w-14 items-center justify-center rounded-md p-2 transition-colors',
                  active ? 'bg-white text-slate-600 shadow-sm' : 'text-slate-400 hover:text-slate-900',
                )}
              >
                <Icon className="h-[34px] w-[34px]" />
              </button>
            );
          })}
        </div>

        {/* Invoices and credit notes, newest first — who issued what, and when
            it was reversed. The approval stamps live on the ORDER, so "who
            approved this one" stays on the order's own history rather than
            dragging every order change into a finance screen. */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <TmsHistoryButton
            organisationId={orgId}
            aggregateType="Invoice"
            wholeType
            subject="Invoicing"
            entityLabel={(entityType) =>
              entityType === 'InvoiceLine' ? 'Invoice line' : entityType
            }
            variant="subtle"
          />
        </div>
      </div>

      {/* Content area — also the positioning context for the assistant, so the
          panel starts at the separation line (below the tabs), like Manifesting. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <main className="flex min-h-0 flex-1 flex-col px-2 pt-3">
          {currentTab === 'approval' && <ForApprovalTab onOpenAssistant={openAssistant} />}
          {currentTab === 'create' && <CreateInvoicesTab onOpenAssistant={openAssistant} />}
          {currentTab === 'summary' && <InvoiceSummaryTab onOpenAssistant={openAssistant} />}
        </main>

        <FinanceAssistantPanel
          open={assistantOpen}
          tab={currentTab}
          onClose={() => setAssistantOpen(false)}
        />
      </div>
    </div>
  );
}
